import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Wrench, AlertTriangle, CheckCircle2, Clock, CalendarDays, Loader2, Bell } from 'lucide-react';
import api from '../api';
import { INTERVENTION_STATUS, URGENCY_LEVELS, formatDate } from '../constants';

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-xl border border-neutral-200 p-5" data-testid={`stat-${label}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-3xl font-bold text-neutral-900">{value ?? 0}</p>
        <p className="text-sm text-neutral-500 mt-1">{label}</p>
      </div>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, n] = await Promise.all([
          api.get('/interventions/stats'),
          api.get('/interventions'),
          api.get('/notifications'),
        ]);
        setStats(s.data);
        setRecent(r.data.slice(0, 6));
        setNotifs(n.data.items.slice(0, 5));
      } catch (e) {
        // géré par interceptor
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-neutral-900 mb-1">Tableau de bord</h1>
      <p className="text-neutral-500 mb-6">Vue d'ensemble de l'activité</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Wrench} label="Nouvelles demandes" value={stats?.nouvelles} accent="bg-blue-100 text-blue-700" />
        <StatCard icon={AlertTriangle} label="Urgentes" value={stats?.urgentes} accent="bg-red-100 text-red-700" />
        <StatCard icon={Clock} label="En cours" value={stats?.en_cours} accent="bg-yellow-100 text-yellow-700" />
        <StatCard icon={CheckCircle2} label="Terminées" value={stats?.terminees} accent="bg-green-100 text-green-700" />
        <StatCard icon={CalendarDays} label="Aujourd'hui" value={stats?.today} accent="bg-indigo-100 text-indigo-700" />
        <StatCard icon={Wrench} label="À planifier" value={stats?.a_planifier} accent="bg-purple-100 text-purple-700" />
        <StatCard icon={Users} label="Clients" value={stats?.clients} accent="bg-neutral-200 text-neutral-700" />
        <StatCard icon={Wrench} label="Total" value={stats?.total} accent="bg-neutral-200 text-neutral-700" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-neutral-900">Demandes récentes</h2>
            <Link to="/admin/interventions" className="text-sm text-red-700 hover:underline" data-testid="see-all-interventions">Voir tout</Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-neutral-400 text-sm py-8 text-center">Aucune demande pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {recent.map((it) => {
                const st = INTERVENTION_STATUS[it.status] || {};
                const ur = URGENCY_LEVELS[it.urgency] || {};
                return (
                  <Link key={it.id} to={`/admin/interventions/${it.id}`} data-testid={`dashboard-intervention-${it.number}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 border border-neutral-100">
                    <div className="min-w-0">
                      <p className="font-medium text-neutral-900 text-sm">{it.number} — {it.clientName}</p>
                      <p className="text-xs text-neutral-500 truncate">{it.city || ''} · {formatDate(it.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded ${ur.color}`}>{ur.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-4 h-4 text-red-700" />
            <h2 className="font-bold text-neutral-900">Notifications</h2>
          </div>
          {notifs.length === 0 ? (
            <p className="text-neutral-400 text-sm py-8 text-center">Aucune notification.</p>
          ) : (
            <div className="space-y-3">
              {notifs.map((n) => (
                <Link key={n.id + n.createdAt} to={n.link} className="block p-3 rounded-lg bg-neutral-50 hover:bg-neutral-100" data-testid="notification-item">
                  <p className="text-sm font-medium text-neutral-900">{n.title}</p>
                  <p className="text-xs text-neutral-500">{n.message}</p>
                  <p className="text-xs text-neutral-400 mt-1">{formatDate(n.createdAt)}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
